import { useEffect, useMemo } from "react";
import { FormContext } from "zeta-dom-react";
import { OptionItem } from "src/api/types";
import { app } from "src/core/app";
import { DownlineAgentFilterType, DownlineAgentFilterTypes } from "src/core/constants";
import { translate as t } from "src/i18n";
import { Dropdown, DropdownItem, RadioList, TextField } from ".";

const typeOptions = {
    district: { get label() { return t('common.downlineFilter.option.district') }, value: DownlineAgentFilterTypes.district },
    unit: { get label() { return t('common.downlineFilter.option.unit') }, value: DownlineAgentFilterTypes.unit },
    agency: { get label() { return t('common.downlineFilter.option.agency') }, value: DownlineAgentFilterTypes.agency },
    agent: { get label() { return t('common.downlineFilter.option.agent') }, value: DownlineAgentFilterTypes.agent }
};

export interface DownlineAgentFilterValues {
    filterType: DownlineAgentFilterType;
    filterValue?: string;
    agentId?: string;
}

export interface DownlineAgentFilterOptions {
    districts: OptionItem[];
    units: OptionItem[];
    agencies: OptionItem[];
}

export interface DownlineAgentFilterProps {
    form: FormContext<DownlineAgentFilterValues>;
    options?: DownlineAgentFilterOptions;
}

function getFilterTypeList(options: DownlineAgentFilterOptions | undefined): DropdownItem[] {
    const list = [];
    if (options) {
        if (options.districts.length) {
            list.push(typeOptions.district);
        }
        if (options.units.length) {
            list.push(typeOptions.unit);
        }
        if (options.agencies.length) {
            list.push(typeOptions.agency);
        }
        list.push(typeOptions.agent);
    }
    return list;
}

function getFilterValueList(options: DownlineAgentFilterOptions | undefined, filterType: DownlineAgentFilterType | undefined) {
    if (options) {
        switch (filterType) {
            case DownlineAgentFilterTypes.district:
                return options.districts;
            case DownlineAgentFilterTypes.unit:
                return options.units;
            case DownlineAgentFilterTypes.agency:
                return options.agencies;
        }
    }
    return [];
}

export function DownlineAgentFilter(props: DownlineAgentFilterProps) {
    const { form, options } = props;
    const filterTypeList = useMemo(() => getFilterTypeList(options), [options]);
    const filterValueList = useMemo(() => getFilterValueList(options, form.data.filterType), [options, form.data.filterType]);

    useEffect(() => {
        if (filterTypeList.length && !form.data.filterType) {
            form.data.filterType = app.auth.currentUser?.agentType !== 'dd' ? DownlineAgentFilterTypes.agent : filterTypeList[0].value as any;
        }
        if (form.data.filterType === DownlineAgentFilterTypes.agent) {
            form.data.filterValue = form.data.agentId;
            return form.on('dataChange', e => {
                if (e.data.includes('agentId')) {
                    form.data.filterValue = form.data.agentId;
                }
            });
        }
    }, [form, filterTypeList, form.data.filterType]);

    const agentOnly = filterTypeList.length === 1;
    const label = filterTypeList.find(v => v.value === form.data.filterType)?.label;
    return (
        <div className="eac-downline-agent-filter">
            {!agentOnly &&
                <RadioList name="filterType" items={filterTypeList} />}
            {form.data.filterType !== DownlineAgentFilterTypes.agent ?
                <Dropdown name="filterValue" allowUnselect={true} required={true} items={filterValueList} label={label} /> :
                <TextField name="agentId" required={true} label={label} disabled={agentOnly} />}
        </div>
    );
}
